import * as THREE from 'three';
import { useViewScoreStore } from '@/store/viewScoreStore';
import { calculateViewScore } from '@/services/score/view';
import { WorkflowProps } from '@/types/workflow';
import { openScoreModal } from '../AddScoreToRoomModal/AddScoreToRoomModal';

export const handleAddViewScoreModal = (
	roomFillMesh?: THREE.Mesh | null,
	windowObject?: THREE.Object3D | null,
	windowPosition?: THREE.Vector3,
	workflowProps?: WorkflowProps
) => {
	if (!workflowProps) return;

	// Get view store data
	const viewData = useViewScoreStore.getState().viewData;
	const viewSettingsByPoint = useViewScoreStore.getState().viewSettingsByPoint;
	const fallbackHorizonContexts = useViewScoreStore.getState().horizonContexts;
	const fallbackBeyondHorizonEnabled = useViewScoreStore.getState().isBeyondHorizonEnabled;

	// Get view analysis data for this window
	let windowViewData = null;
	let windowDataKey: string | null = null;
	if (windowPosition) {
		// Try to find view data using the window position as key
		const posKey = `${windowPosition.x},${windowPosition.y},${windowPosition.z}`;
		windowViewData = viewData[posKey];
		if (windowViewData) {
			windowDataKey = posKey;
		}

		// If exact match not found, try to find by window UUID
		if (!windowViewData && windowObject?.userData.windowUuid) {
			// Search for data by window UUID in the keys
			for (const [key, data] of Object.entries(viewData)) {
				if (key.includes(windowObject.userData.windowUuid)) {
					windowViewData = data;
					windowDataKey = key;
					break;
				}
			}
		}
	}

	const settingsAtAnalysis = windowDataKey ? viewSettingsByPoint[windowDataKey] : undefined;

	const viewScoreBreakdown =
		windowViewData && Object.keys(windowViewData).length > 0
			? calculateViewScore(
				windowViewData,
				settingsAtAnalysis?.horizonContexts ?? fallbackHorizonContexts,
				settingsAtAnalysis?.isBeyondHorizonEnabled ?? fallbackBeyondHorizonEnabled
			)
			: null;

	if (!viewScoreBreakdown) {
		console.warn('No view analysis data found for this window');
		return;
	}

	// Prepare score data
	const scoreData = {
		totalScore: viewScoreBreakdown.totalScore,
		breakdown: {
			Open: viewScoreBreakdown.openScore.toFixed(1),
			...(viewScoreBreakdown.beyondHorizonEnabled
				? { 'Beyond Horizon Bonus': viewScoreBreakdown.beyondHorizonBonus.toFixed(1) }
				: {}),
			Vegetation: viewScoreBreakdown.vegetationScore.toFixed(1),
			Water: viewScoreBreakdown.waterScore.toFixed(1),
			'Sky View': viewScoreBreakdown.skyViewFactor.toFixed(1),
			Diversity: viewScoreBreakdown.viewDiversity.toFixed(1),
			Horizontal: viewScoreBreakdown.horizontalOpenness.toFixed(1),
		},
		data: windowViewData, // Include raw view data to attach to room
		fullBreakdown: viewScoreBreakdown, // Include full breakdown for recommendations
	};

	openScoreModal({
		scoreType: 'view',
		roomFillMesh: roomFillMesh ?? undefined,
		scoreData,
		workflowProps,
	});
};
